import { EnergyType } from '../../EnergyFlow'
import { getTemperaturesFromCurve } from '../util/temperature-curve-utility'
import { reduceToSum } from '../../../util/array-utility'
import {
  HeatCircuit,
  TemperatureCurveFragment,
} from '../../../generated/graphql'
import { PrimaryHourlyEnergyFlow } from '../../HourlyEnergyFlow'
import { calculatePowerCoverageFactor } from './pre-calculation-util'

export type PreCalculationParams = {
  temperatureCurvesById: Record<string, TemperatureCurveFragment>
  basicHeatCircuitsByDemandId: Record<string, HeatCircuit>
  dhwHeatCircuitsByDemandId: Record<string, HeatCircuit>
  heatingTemperatureCurveId?: string
  coolingTemperatureCurveId?: string
  remainingDemands: PrimaryHourlyEnergyFlow[]
  outdoorTemperature: number
}

type HeatingResults = {
  basic: {
    totalDemand: number
    powerCoverageFactors: number[]
    temperature: number
  }
  dhw: {
    totalDemand: number
    powerCoverageFactors: number[]
    temperature: number
  }
}

type CoolingResults = {
  totalDemand: number
  temperature: number
}

export type PreCalculationResults = {
  heating?: HeatingResults
  cooling?: CoolingResults
}

function preCalculateHeatingForHour(
  temperatureCurvesById: Record<string, TemperatureCurveFragment>,
  basicHeatCircuitsByDemandId: Record<string, HeatCircuit>,
  dhwHeatCircuitsByDemandId: Record<string, HeatCircuit>,
  heatingTemperatureCurveId: string,
  heatingDemands: PrimaryHourlyEnergyFlow[],
  outdoorTemperature: number,
): HeatingResults {
  const curveTemperature = getTemperaturesFromCurve(
    temperatureCurvesById[heatingTemperatureCurveId],
    outdoorTemperature,
  )
  const temperature = Math.max(curveTemperature.in, curveTemperature.return)

  const basicHeatingDemands = heatingDemands.filter(
    (demand) => basicHeatCircuitsByDemandId[demand.demandId],
  )
  const dhwHeatingDemands = heatingDemands.filter(
    (demand) => dhwHeatCircuitsByDemandId[demand.demandId],
  )

  // Temperatures

  const getCircuitTemperature = (circuit: HeatCircuit) => {
    const circuitTemperatureCurve =
      temperatureCurvesById[circuit.temperatureCurveId]
    return getTemperaturesFromCurve(circuitTemperatureCurve, outdoorTemperature)
  }

  const basicCircuitCurveTemperatures = basicHeatingDemands.map((demand) =>
    getCircuitTemperature(basicHeatCircuitsByDemandId[demand.demandId]),
  )
  const dhwCircuitCurveTemperatures = dhwHeatingDemands.map((demand) =>
    getCircuitTemperature(dhwHeatCircuitsByDemandId[demand.demandId]),
  )

  const dhwCircuitTemperatures = dhwCircuitCurveTemperatures.map((t) =>
    Math.max(t.in, t.return),
  )

  // Power coverage factors

  const basicPowerCoverageFactors = basicHeatingDemands.map((demand, index) =>
    calculatePowerCoverageFactor(
      temperature,
      basicCircuitCurveTemperatures[index],
    ),
  )
  const dhwPowerCoverageFactors = dhwHeatingDemands.map(() => 1)

  // Total demands

  const totalBasicDemand = basicHeatingDemands
    .map((demand, index) => demand.value * basicPowerCoverageFactors[index])
    .reduce(reduceToSum, 0)
  const totalDhwDemand = dhwHeatingDemands
    .map((demand, index) => demand.value * dhwPowerCoverageFactors[index])
    .reduce(reduceToSum, 0)

  return {
    basic: {
      totalDemand: totalBasicDemand,
      powerCoverageFactors: basicPowerCoverageFactors,
      temperature,
    },
    dhw: {
      totalDemand: totalDhwDemand,
      powerCoverageFactors: dhwPowerCoverageFactors,
      temperature:
        dhwCircuitTemperatures.length > 0
          ? Math.max(...dhwCircuitTemperatures)
          : NaN,
    },
  }
}

function preCalculateCoolingForHour(
  temperatureCurvesById: Record<string, TemperatureCurveFragment>,
  coolingTemperatureCurveId: string,
  coolingDemands: PrimaryHourlyEnergyFlow[],
  outdoorTemperature: number,
): CoolingResults {
  // Temperature

  const curveTemperature = getTemperaturesFromCurve(
    temperatureCurvesById[coolingTemperatureCurveId],
    outdoorTemperature,
  )
  const temperature = Math.min(curveTemperature.in, curveTemperature.return)

  // Total demand

  const totalDemand = coolingDemands
    .map((demand) => demand.value)
    .reduce(reduceToSum, 0)

  return {
    totalDemand,
    temperature,
  }
}

export function preCalculateForHour({
  temperatureCurvesById,
  basicHeatCircuitsByDemandId,
  dhwHeatCircuitsByDemandId,
  heatingTemperatureCurveId,
  coolingTemperatureCurveId,
  remainingDemands,
  outdoorTemperature,
}: PreCalculationParams): PreCalculationResults {
  const heatingResults = heatingTemperatureCurveId
    ? preCalculateHeatingForHour(
        temperatureCurvesById,
        basicHeatCircuitsByDemandId,
        dhwHeatCircuitsByDemandId,
        heatingTemperatureCurveId,
        remainingDemands.filter(
          (demand) => demand.energyType === EnergyType.Heating,
        ),
        outdoorTemperature,
      )
    : undefined

  const coolingResults = coolingTemperatureCurveId
    ? preCalculateCoolingForHour(
        temperatureCurvesById,
        coolingTemperatureCurveId,
        remainingDemands.filter(
          (demand) => demand.energyType === EnergyType.Cooling,
        ),
        outdoorTemperature,
      )
    : undefined

  return {
    heating: heatingResults,
    cooling: coolingResults,
  }
}
